import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { firstValueFrom, map, Observable, Subscription } from "rxjs";
import { environment } from "src/environments/environment";

import ApiVersionConstants from "../constants/api-version-constants";
import { LoginStatus } from "../models/Enums";
import { UserProfile } from "../models/UserProfile";
import { Region } from "./../models/Region";
import { AuthService } from "./auth.service";
import { CompanyService } from "./company.service";
import { StorageService } from "./storage.service";

@Injectable({
  providedIn: "root",
})
export class RegionService implements OnDestroy {
  private baseUrl = `${environment.apiConfig.uri}/${ApiVersionConstants.V_2}/region`;
  private _subscriptions$: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private authService: AuthService,
    private companyService: CompanyService,
  ) {
    this.onLoginChange();
  }

  public getAll(): Observable<Region[]> {
    return this.http.get<Region[]>(this.baseUrl);
  }

  public set(region: Region): Observable<UserProfile> {
    return this.http
      .post<UserProfile>(this.baseUrl, region)
      .pipe(map((x) => new UserProfile().deserialize(x)));
  }

  public setRegionToStorage(region: string): void {
    if (!region) {
      return;
    }

    this.storage.setRegion(region);
  }

  public getCurrentRegion(): Region {
    const region = this.storage.getRegion();

    if (region) {
      const regionObj: Region = { region: region, isCurrentRegion: true };
      return regionObj;
    }
  }

  private onLoginChange() {
    const subscription = this.authService.isUserProfileSet$.subscribe(
      (userProfileSet: LoginStatus) => {
        if (userProfileSet === LoginStatus.LoggedIn) {
          this.setRegionFromStorageAsync();
        }
      },
    );

    this._subscriptions$.push(subscription);
  }

  private async setRegionFromStorageAsync(): Promise<void> {
    // Set the region in case user is authorized
    // since regionController requires authorization
    if (!this.authService.isLoggedIn) {
      return;
    }

    // if we don't have a company select, then don't try to set the region
    if (!this.companyService.selectedCompany) {
      return;
    }

    const region = this.getCurrentRegion();
    const allRegions = await firstValueFrom(this.getAll());
    const currentRegionOnBE = allRegions.find((x) => x.isCurrentRegion);

    if (!region) {
      this.setRegionFromBE(currentRegionOnBE);
      return;
    }

    // the region on api is the same as region on client, no action
    if (region.region === currentRegionOnBE.region) {
      return;
    }

    // if user's client region is different from BE region
    // and it's not on the list available region, then just set
    // the region from BE on client
    if (allRegions.every((x) => x.region !== region.region)) {
      this.setRegionFromBE(currentRegionOnBE);
      return;
    }

    // otherwise set the region from client on BE
    const user = await firstValueFrom(this.set(region));
    this.authService.onUserProfileRetrieve(user);
  }

  private setRegionFromBE(regionFromBE: Region): void {
    // default case set the  currentRegion from BE on client side
    this.setRegionToStorage(regionFromBE.region);

    // remove the selected company because it's for a wrong region
    this.storage.cleanCompanyStorage();
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((a) => a.unsubscribe());
  }
}
